import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatEUR } from "@/lib/constants";

export async function downloadSepa(orderIds) {
  const { data } = await api.post("/payout-orders/sepa-export", { order_ids: orderIds }, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SEPA-Ueberweisungen-${new Date().toISOString().slice(0, 10)}.xml`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>
);

export function SurplusRefundDialog({ open, onClose, tenancyId, onDone }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [notify, setNotify] = useState(true);
  const [sepaNow, setSepaNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setD(null);
    setNotify(true);
    setSepaNow(true);
    api.get(`/payout-orders/refund-defaults/${tenancyId}`).then(({ data }) => {
      setD(data);
      setForm({ amount: data.amount, creditor_name: data.creditor.name, creditor_iban: data.creditor.iban,
                creditor_bic: data.creditor.bic, purpose: data.purpose, execution_date: data.execution_date });
    }).catch((e) => toast.error(e?.response?.data?.detail || "Daten konnten nicht geladen werden"));
  }, [open, tenancyId]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payout-orders/surplus-refund", {
        tenancy_id: tenancyId, ...form, amount: Number(form.amount), notify,
      });
      if (sepaNow) await downloadSepa([data.order.id]);
      toast.success(`Erstattung ${formatEUR(data.order.amount)} angelegt` + (sepaNow ? " · SEPA-Datei heruntergeladen" : " · Auftrag unter Bank → Zahlungsaufträge")
        + (data.notice?.status === "sent" ? ` · Mitteilung per ${data.notice.channel === "email" ? "E-Mail" : "Brief"}` : ""));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erstattung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const tooMuch = d && Number(form.amount) > d.amount + 0.005;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="surplus-refund-dialog">
        <DialogHeader>
          <DialogTitle>Guthaben erstatten</DialogTitle>
          <DialogDescription>
            Legt die Erstattung im Mietkonto an und erzeugt einen SEPA-Zahlungsauftrag. Beim Bankimport wird die Überweisung automatisch zugeordnet.
          </DialogDescription>
        </DialogHeader>
        {!d ? <div className="text-sm text-slate-500 py-4">Lädt…</div> : (
          <div className="space-y-3">
            <div className="text-xs text-slate-600" data-testid="refund-free-surplus">Freies Guthaben: <b>{formatEUR(d.amount)}</b></div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Betrag (€)">
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                       className={tooMuch ? "border-rose-400" : ""} data-testid="refund-amount" />
              </Field>
              <Field label="Ausführungsdatum">
                <Input type="date" value={form.execution_date} onChange={(e) => set("execution_date", e.target.value)} data-testid="refund-date" />
              </Field>
            </div>
            <Field label="Empfänger (Kontoinhaber)">
              <Input value={form.creditor_name} onChange={(e) => set("creditor_name", e.target.value)} data-testid="refund-creditor-name" />
            </Field>
            <div className="grid grid-cols-[2fr_1fr] gap-3">
              <Field label="IBAN Empfänger">
                <Input value={form.creditor_iban} onChange={(e) => set("creditor_iban", e.target.value)} className="font-mono" data-testid="refund-creditor-iban" />
              </Field>
              <Field label="BIC (optional)">
                <Input value={form.creditor_bic || ""} onChange={(e) => set("creditor_bic", e.target.value)} className="font-mono" data-testid="refund-creditor-bic" />
              </Field>
            </div>
            <Field label="Verwendungszweck">
              <Input value={form.purpose} maxLength={140} onChange={(e) => set("purpose", e.target.value)} data-testid="refund-purpose" />
            </Field>
            <div className="text-[11px] text-slate-500" data-testid="refund-debtor">
              Auftraggeber-Konto: {d.debtor.iban ? `${d.debtor.name} · ${d.debtor.iban}` : <span className="text-rose-700">keines hinterlegt (Mietkonto am MV oder Bankverbindung am Objekt)</span>}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="refund-notify" />
              Mitteilung an den Mieter (E-Mail, sonst Brief im Schriftverkehr)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={sepaNow} onCheckedChange={(v) => setSepaNow(!!v)} data-testid="refund-sepa-now" />
              SEPA-Datei sofort herunterladen
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !d || tooMuch || !(Number(form.amount) > 0)}
                  className="bg-emerald-700 hover:bg-emerald-800" data-testid="refund-submit">
            {busy ? "Speichert…" : "Erstattung anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
